// ============================================================
// Motor de cálculo de riesgo ergonómico — Método REBA
// (Rapid Entire Body Assessment)
//
// Fuente: Hignett, S. & McAtamney, L. (2000). Rapid entire body
// assessment (REBA). Applied Ergonomics, 31(2), 201-205.
//
// Motor de cálculo puro, sin UI:
//   - Puntuación de tronco, cuello y piernas (Grupo A)
//   - Puntuación de brazo, antebrazo y muñeca (Grupo B)
//   - Puntuación del Grupo A (Tabla 12) y Grupo B (Tabla 13)
//   - Puntuación A (Grupo A + carga/fuerza) y B (Grupo B + agarre)
//   - Puntuación C (Tabla 18, Puntuación A x Puntuación B)
//   - Puntuación final REBA (C + actividad, Tabla 20) y nivel de
//     actuación (Tabla 21)
//
// Las Tablas 12, 13 y 18 (doble/triple entrada) fueron extraídas de la
// fuente y confirmadas celda por celda contra la fuente original por el
// usuario (2026) antes de implementarlas — no modificar esas tablas sin
// repetir esa verificación manual.
// ============================================================

function clamp(valor: number, min: number, max: number): number {
  return Math.min(Math.max(valor, min), max);
}

// ===== 1. Puntuación del tronco (Grupo A) =====

export type RangoTronco =
  | 'erguido'                             // sin flexión/extensión → 1
  | 'flexion_extension_0_20'               // flexión o extensión 0°-20° → 2
  | 'flexion_20_60_o_extension_20'         // flexión 20°-60° o extensión >20° → 3
  | 'flexion_mayor_60';                    // flexión >60° → 4

export interface ModificadoresTronco {
  inclinacionLateralORotacion?: boolean; // +1
}

const PUNTUACION_BASE_TRONCO: Record<RangoTronco, number> = {
  erguido: 1,
  flexion_extension_0_20: 2,
  flexion_20_60_o_extension_20: 3,
  flexion_mayor_60: 4,
};

// REBA: inclinación lateral y rotación del tronco se registran como un
// único modificador (+1), no se suman por separado.
export function calcularPuntuacionTronco(
  rango: RangoTronco,
  modificadores: ModificadoresTronco
): number {
  let puntuacion = PUNTUACION_BASE_TRONCO[rango];
  if (modificadores.inclinacionLateralORotacion) puntuacion += 1;
  return puntuacion;
}

// ===== 2. Puntuación del cuello (Grupo A) =====

export type RangoCuello =
  | 'flexion_0_20'              // flexión 0°-20° → 1
  | 'flexion_mayor_20_o_extension'; // flexión >20° o cualquier extensión → 2

export interface ModificadoresCuello {
  inclinacionLateralORotacion?: boolean; // +1
}

const PUNTUACION_BASE_CUELLO: Record<RangoCuello, number> = {
  flexion_0_20: 1,
  flexion_mayor_20_o_extension: 2,
};

// REBA: inclinación lateral y rotación del cuello se registran como un
// único modificador (+1), no se suman por separado.
export function calcularPuntuacionCuello(
  rango: RangoCuello,
  modificadores: ModificadoresCuello
): number {
  let puntuacion = PUNTUACION_BASE_CUELLO[rango];
  if (modificadores.inclinacionLateralORotacion) puntuacion += 1;
  return puntuacion;
}

// ===== 3. Puntuación de las piernas (Grupo A) =====

export type CondicionPiernas =
  | 'soporte_bilateral_simetrico'   // soporte bilateral, de pie o sentado,
                                      // caminando de forma simétrica → 1
  | 'soporte_unilateral_o_inestable'; // soporte unilateral, ligero o
                                        // inestable → 2

export interface ModificadoresPiernas {
  // Flexión de rodillas. 'mayor_60' no aplica si la postura es sedente
  // — el motor no valida esa combinación, se deja al formulario.
  flexionRodillas?: 'ninguna' | '30_60' | 'mayor_60';
}

const PUNTUACION_BASE_PIERNAS: Record<CondicionPiernas, number> = {
  soporte_bilateral_simetrico: 1,
  soporte_unilateral_o_inestable: 2,
};

const MODIFICADOR_FLEXION_RODILLAS: Record<'ninguna' | '30_60' | 'mayor_60', number> = {
  ninguna: 0,
  '30_60': 1,
  mayor_60: 2,
};

export function calcularPuntuacionPiernas(
  condicion: CondicionPiernas,
  modificadores: ModificadoresPiernas
): number {
  const base = PUNTUACION_BASE_PIERNAS[condicion];
  const flexion = modificadores.flexionRodillas ?? 'ninguna';
  return base + MODIFICADOR_FLEXION_RODILLAS[flexion];
}

// ===== 4. Puntuación del brazo (Grupo B) =====

export type RangoBrazo =
  | 'extension_20_a_flexion_20'              // extensión 20° a flexión 20° → 1
  | 'extension_mayor_20_o_flexion_20_45'     // extensión >20° o flexión 20°-45° → 2
  | 'flexion_45_90'                           // flexión 45°-90° → 3
  | 'flexion_mayor_90';                       // flexión >90° → 4

export interface ModificadoresBrazo {
  abducidoORotado?: boolean;         // +1
  hombroElevado?: boolean;           // +1
  puntoApoyoOFavorGravedad?: boolean; // -1
}

const PUNTUACION_BASE_BRAZO: Record<RangoBrazo, number> = {
  extension_20_a_flexion_20: 1,
  extension_mayor_20_o_flexion_20_45: 2,
  flexion_45_90: 3,
  flexion_mayor_90: 4,
};

// REBA: los 3 modificadores del brazo NO son excluyentes entre sí, se
// pueden combinar y sumar/restar todos los que apliquen.
export function calcularPuntuacionBrazo(
  rango: RangoBrazo,
  modificadores: ModificadoresBrazo
): number {
  let puntuacion = PUNTUACION_BASE_BRAZO[rango];
  if (modificadores.abducidoORotado) puntuacion += 1;
  if (modificadores.hombroElevado) puntuacion += 1;
  if (modificadores.puntoApoyoOFavorGravedad) puntuacion -= 1;
  return puntuacion;
}

// ===== 5. Puntuación del antebrazo (Grupo B) =====

export type RangoAntebrazo =
  | 'flexion_60_100'            // flexión 60°-100° → 1
  | 'flexion_menor_60_o_mayor_100'; // flexión <60° o >100° → 2

const PUNTUACION_BASE_ANTEBRAZO: Record<RangoAntebrazo, number> = {
  flexion_60_100: 1,
  flexion_menor_60_o_mayor_100: 2,
};

// REBA: el antebrazo no tiene modificadores.
export function calcularPuntuacionAntebrazo(rango: RangoAntebrazo): number {
  return PUNTUACION_BASE_ANTEBRAZO[rango];
}

// ===== 6. Puntuación de la muñeca (Grupo B) =====

export type RangoMuneca =
  | 'neutra_o_flexion_extension_menor_15' // neutra o flexión/extensión <15° → 1
  | 'flexion_extension_mayor_15';          // flexión o extensión >15° → 2

export interface ModificadoresMuneca {
  torsionODesviacion?: boolean; // +1
}

const PUNTUACION_BASE_MUNECA: Record<RangoMuneca, number> = {
  neutra_o_flexion_extension_menor_15: 1,
  flexion_extension_mayor_15: 2,
};

export function calcularPuntuacionMuneca(
  rango: RangoMuneca,
  modificadores: ModificadoresMuneca
): number {
  let puntuacion = PUNTUACION_BASE_MUNECA[rango];
  if (modificadores.torsionODesviacion) puntuacion += 1;
  return puntuacion;
}

// ===== 7. Puntuación del Grupo A (Tabla 12) =====

// Tabla 12 REBA: tabla de triple entrada Cuello (1-3) x Tronco (1-5) x
// Piernas (1-4). Extraída de Hignett, S. & McAtamney, L. (2000). Rapid
// entire body assessment (REBA). Applied Ergonomics, 31(2), 201-205, y
// confirmada celda por celda contra la fuente por el usuario (2026) —
// no modificar sin repetir esa verificación.
const TABLA_12_GRUPO_A: number[][][] = [
  // Cuello 1
  [
    [1, 2, 3, 4], // Tronco 1
    [2, 3, 4, 5], // Tronco 2
    [2, 4, 5, 6], // Tronco 3
    [3, 5, 6, 7], // Tronco 4
    [4, 6, 7, 8], // Tronco 5
  ],
  // Cuello 2
  [
    [1, 2, 3, 4], // Tronco 1
    [3, 4, 5, 6], // Tronco 2
    [4, 5, 6, 7], // Tronco 3
    [5, 6, 7, 8], // Tronco 4
    [6, 7, 8, 9], // Tronco 5
  ],
  // Cuello 3
  [
    [3, 3, 5, 6], // Tronco 1
    [4, 5, 6, 7], // Tronco 2
    [5, 6, 7, 8], // Tronco 3
    [6, 7, 8, 9], // Tronco 4
    [7, 8, 9, 9], // Tronco 5
  ],
];

export function calcularPuntuacionGrupoA(
  tronco: number,
  cuello: number,
  piernas: number
): number {
  const t = clamp(tronco, 1, 5);
  const c = clamp(cuello, 1, 3);
  const p = clamp(piernas, 1, 4);
  return TABLA_12_GRUPO_A[c - 1][t - 1][p - 1];
}

// ===== 8. Puntuación del Grupo B (Tabla 13) =====

// Tabla 13 REBA: tabla de triple entrada Antebrazo (1-2) x Brazo (1-6) x
// Muñeca (1-3). Extraída de Hignett, S. & McAtamney, L. (2000). Rapid
// entire body assessment (REBA). Applied Ergonomics, 31(2), 201-205, y
// confirmada celda por celda contra la fuente por el usuario (2026) —
// no modificar sin repetir esa verificación.
const TABLA_13_GRUPO_B: number[][][] = [
  // Antebrazo 1
  [
    [1, 2, 2], // Brazo 1
    [1, 2, 3], // Brazo 2
    [3, 4, 5], // Brazo 3
    [4, 5, 5], // Brazo 4
    [6, 7, 8], // Brazo 5
    [7, 8, 8], // Brazo 6
  ],
  // Antebrazo 2
  [
    [1, 2, 3], // Brazo 1
    [2, 3, 4], // Brazo 2
    [4, 5, 5], // Brazo 3
    [5, 6, 7], // Brazo 4
    [7, 8, 8], // Brazo 5
    [8, 9, 9], // Brazo 6
  ],
];

export function calcularPuntuacionGrupoB(
  brazo: number,
  antebrazo: number,
  muneca: number
): number {
  const b = clamp(brazo, 1, 6);
  const a = clamp(antebrazo, 1, 2);
  const m = clamp(muneca, 1, 3);
  return TABLA_13_GRUPO_B[a - 1][b - 1][m - 1];
}

// ===== 9. Modificadores de carga/fuerza (Grupo A) y agarre (Grupo B) =====

export type CargaFuerza =
  | 'menor_5kg'      // carga <5kg → 0
  | 'entre_5_10kg'   // carga 5-10kg → +1
  | 'mayor_10kg';    // carga >10kg → +2

const PUNTUACION_CARGA_FUERZA: Record<CargaFuerza, number> = {
  menor_5kg: 0,
  entre_5_10kg: 1,
  mayor_10kg: 2,
};

export type CalidadAgarre = 'bueno' | 'regular' | 'malo' | 'inaceptable';

const PUNTUACION_AGARRE: Record<CalidadAgarre, number> = {
  bueno: 0,
  regular: 1,
  malo: 2,
  inaceptable: 3,
};

// REBA: Puntuación A = Puntuación Grupo A + carga/fuerza + 1 adicional
// si la fuerza se aplica de forma brusca o repentina.
export function calcularPuntuacionA(
  grupoA: number,
  cargaFuerza: CargaFuerza,
  fuerzaBrusca: boolean
): number {
  let puntuacion = grupoA + PUNTUACION_CARGA_FUERZA[cargaFuerza];
  if (fuerzaBrusca) puntuacion += 1;
  return puntuacion;
}

// REBA: Puntuación B = Puntuación Grupo B + calidad del agarre.
export function calcularPuntuacionB(grupoB: number, calidadAgarre: CalidadAgarre): number {
  return grupoB + PUNTUACION_AGARRE[calidadAgarre];
}

// ===== 10. Puntuación C (Tabla 18) =====

// Tabla 18 REBA: tabla de doble entrada Puntuación A (filas 1-12) x
// Puntuación B (columnas 1-12). Extraída de Hignett, S. & McAtamney, L.
// (2000). Rapid entire body assessment (REBA). Applied Ergonomics, 31(2),
// 201-205, y confirmada celda por celda contra la fuente por el usuario
// (2026) — no modificar sin repetir esa verificación.
const TABLA_18_PUNTUACION_C: number[][] = [
  [1, 1, 1, 2, 3, 3, 4, 5, 6, 7, 7, 7],       // A=1
  [1, 2, 2, 3, 4, 4, 5, 6, 6, 7, 7, 8],       // A=2
  [2, 3, 3, 3, 4, 5, 6, 7, 7, 8, 8, 8],       // A=3
  [3, 4, 4, 4, 5, 6, 7, 8, 8, 9, 9, 9],       // A=4
  [4, 4, 4, 5, 6, 7, 8, 8, 9, 9, 9, 9],       // A=5
  [6, 6, 6, 7, 8, 8, 9, 9, 10, 10, 10, 10],   // A=6
  [7, 7, 7, 8, 9, 9, 9, 10, 10, 11, 11, 11],  // A=7
  [8, 8, 8, 9, 10, 10, 10, 10, 10, 11, 11, 11], // A=8
  [9, 9, 9, 10, 10, 10, 11, 11, 11, 12, 12, 12], // A=9
  [10, 10, 10, 11, 11, 11, 11, 12, 12, 12, 12, 12], // A=10
  [11, 11, 11, 11, 12, 12, 12, 12, 12, 12, 12, 12], // A=11
  [12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12], // A=12
];

export function calcularPuntuacionC(puntuacionA: number, puntuacionB: number): number {
  const a = clamp(puntuacionA, 1, 12);
  const b = clamp(puntuacionB, 1, 12);
  return TABLA_18_PUNTUACION_C[a - 1][b - 1];
}

// ===== 11. Puntuación por actividad muscular (Tabla 20) =====

export interface TipoActividadReba {
  posturaEstaticaMasDe1Min?: boolean;              // +1
  movimientosRepetitivosMasDe4PorMin?: boolean;    // +1
  cambiosPosturaImportantesOInestable?: boolean;   // +1
}

// Tabla 20 REBA: los 3 factores de actividad NO son excluyentes, se
// pueden combinar y sumar todos los que apliquen (máximo +3).
function calcularPuntuacionActividad(actividad: TipoActividadReba): number {
  let puntuacion = 0;
  if (actividad.posturaEstaticaMasDe1Min) puntuacion += 1;
  if (actividad.movimientosRepetitivosMasDe4PorMin) puntuacion += 1;
  if (actividad.cambiosPosturaImportantesOInestable) puntuacion += 1;
  return puntuacion;
}

// ===== 12. Puntuación final REBA =====

// REBA: Puntuación final = Puntuación C + puntuación de actividad.
export function calcularPuntuacionFinalReba(
  puntuacionC: number,
  actividad: TipoActividadReba
): number {
  return puntuacionC + calcularPuntuacionActividad(actividad);
}

// ===== 13. Nivel de actuación (Tabla 21) =====

export interface NivelActuacionReba {
  nivel: 0 | 1 | 2 | 3 | 4;
  riesgo: string;
  actuacion: string;
}

// Tabla 21 REBA: niveles de actuación según la puntuación final (1-15).
export function obtenerNivelActuacionReba(puntuacionFinal: number): NivelActuacionReba {
  if (puntuacionFinal <= 1) {
    return { nivel: 0, riesgo: 'Inapreciable', actuacion: 'No es necesaria actuación' };
  }
  if (puntuacionFinal <= 3) {
    return { nivel: 1, riesgo: 'Bajo', actuacion: 'Puede ser necesaria la actuación' };
  }
  if (puntuacionFinal <= 7) {
    return { nivel: 2, riesgo: 'Medio', actuacion: 'Es necesaria la actuación' };
  }
  if (puntuacionFinal <= 10) {
    return { nivel: 3, riesgo: 'Alto', actuacion: 'Es necesaria la actuación cuanto antes' };
  }
  return { nivel: 4, riesgo: 'Muy alto', actuacion: 'Es necesaria la actuación de inmediato' };
}

// ============================================================
// Verificación manual — postura neutra completa (caso de referencia)
// ============================================================
// const tronco = calcularPuntuacionTronco('erguido', {}); // → 1
// const cuello = calcularPuntuacionCuello('flexion_0_20', {}); // → 1
// const piernas = calcularPuntuacionPiernas('soporte_bilateral_simetrico', {}); // → 1
//
// const grupoA = calcularPuntuacionGrupoA(tronco, cuello, piernas);
// // TABLA_12_GRUPO_A[cuello=1-1][tronco=1-1][piernas=1-1] = 1 → grupoA = 1
//
// const brazo = calcularPuntuacionBrazo('extension_20_a_flexion_20', {}); // → 1
// const antebrazo = calcularPuntuacionAntebrazo('flexion_60_100'); // → 1
// const muneca = calcularPuntuacionMuneca('neutra_o_flexion_extension_menor_15', {}); // → 1
//
// const grupoB = calcularPuntuacionGrupoB(brazo, antebrazo, muneca);
// // TABLA_13_GRUPO_B[antebrazo=1-1][brazo=1-1][muneca=1-1] = 1 → grupoB = 1
//
// const puntuacionA = calcularPuntuacionA(grupoA, 'menor_5kg', false); // → 1
// const puntuacionB = calcularPuntuacionB(grupoB, 'bueno'); // → 1
//
// const puntuacionC = calcularPuntuacionC(puntuacionA, puntuacionB);
// // TABLA_18_PUNTUACION_C[A=1-1][B=1-1] = 1 → puntuacionC = 1
//
// const sinActividad: TipoActividadReba = {};
// const puntuacionFinal = calcularPuntuacionFinalReba(puntuacionC, sinActividad); // → 1
//
// const nivel = obtenerNivelActuacionReba(puntuacionFinal);
// // puntuacionFinal=1 ≤ 1 → { nivel: 0, riesgo: 'Inapreciable', ... }
//
// Resultado esperado (confirmado a mano antes de guardar este archivo):
//   tronco=1, cuello=1, piernas=1 → grupoA=1
//   brazo=1, antebrazo=1, muñeca=1 → grupoB=1
//   sin carga ni agarre → A=1, B=1 → C=1 → sin actividad → final=1 →
//   Nivel 0, "Inapreciable" — postura neutra completa, riesgo mínimo.
// ============================================================
